import { useCallback, useEffect, useRef } from "react";
import { SearchableDashboard } from "../constants";
import { eventTracking, MixpanelEvents } from "../utils/userTracking";

const ABANDON_DELAY_MS = 4000;
const MIN_QUERY_LENGTH = 2;

export const useDashboardSearchTracking = (query: string, results: number) => {
  const trimmed = query.trim();

  const trimmedRef = useRef(trimmed);
  const resultsRef = useRef(results);
  const reportedRef = useRef<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  trimmedRef.current = trimmed;
  resultsRef.current = results;

  const clearPendingAbandon = useCallback(() => {
    if (timerRef.current === null) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const reportAbandoned = useCallback(() => {
    const abandonedQuery = trimmedRef.current;
    if (!abandonedQuery || reportedRef.current.has(abandonedQuery)) return;
    reportedRef.current.add(abandonedQuery);
    eventTracking(MixpanelEvents.search_abandoned, { query: abandonedQuery, results: resultsRef.current });
  }, []);

  // Deliberately returns no cleanup: this effect clears the pending timer at the top of every run,
  // which covers a changing query, and leaving the teardown to the unmount effect below is what
  // lets that one still see an outstanding timer and report it.
  useEffect(() => {
    clearPendingAbandon();
    if (trimmed.length < MIN_QUERY_LENGTH || reportedRef.current.has(trimmed)) return;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      reportAbandoned();
    }, ABANDON_DELAY_MS);
  }, [trimmed, clearPendingAbandon, reportAbandoned]);

  useEffect(
    () => () => {
      if (timerRef.current === null) return;
      clearTimeout(timerRef.current);
      timerRef.current = null;
      reportAbandoned();
    },
    [reportAbandoned]
  );

  const trackSelection = useCallback(
    (dashboard: SearchableDashboard) => {
      const selectedQuery = trimmedRef.current;
      clearPendingAbandon();
      if (!selectedQuery) return;
      reportedRef.current.add(selectedQuery);
      eventTracking(MixpanelEvents.search_result_click, {
        query: selectedQuery,
        results: resultsRef.current,
        selected: dashboard.name,
        link: `${window.location.origin}${dashboard.path}`,
      });
    },
    [clearPendingAbandon]
  );

  return { trackSelection };
};
